import { useTranslation } from 'react-i18next'
import {
  mapVersionToVersionCalculator,
  VersionCalculator,
} from './VersionCalculator'
import { useNotifications } from '../notifications/use-notifications'
import {
  EmissionFragment,
  ProjectFragment,
  ProjectVersionFragment,
} from '../generated/graphql'
import { CalculatorBalanceError, CalculatorError } from './calculator-error'
import { reduceToSum } from '../util/array-utility'

export type UseCalculator = {
  getCalculatedEnergies: (
    project: ProjectFragment | null,
    version: ProjectVersionFragment | null,
  ) => VersionCalculator | null
}

function calculateVersionEnergies(
  project: ProjectFragment,
  version: ProjectVersionFragment,
): VersionCalculator {
  const temperatureValues = project.temperatureHourSeries?.values
  const newVersionCalculator = mapVersionToVersionCalculator(
    version,
    temperatureValues,
  )

  // eslint-disable-next-line no-console
  console.log(
    `%c⚙️ Calculation begun for ${version.id}...`,
    'color:lightskyblue',
  )
  const start = Date.now()

  newVersionCalculator.calculateUsage()

  try {
    newVersionCalculator.checkBalance()
    const end = Date.now()

    // eslint-disable-next-line no-console
    console.log(
      `%c✅ Calculation complete for ${version.id} in ${end - start} ms`,
      'color:lightgreen',
    )
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(
      `%c🚨 Balance error in calculation for ${version.id}${
        error instanceof Error ? `: ${error.message}` : ''
      }`,
      'color:lightcoral',
    )

    throw new CalculatorBalanceError()
  }

  return newVersionCalculator
}

export type CalculatedVersion = {
  /** Version id */
  id: string
  /** Version name */
  name: string
  /** Energy calculator for entire version */
  energyCalculator: VersionCalculator
  /** Area of all buildings in version (with multipliers) */
  totalArea: number
  /** Individual building areas (without multipliers) */
  buildingIdsToAreas: Record<string, number>
  /** Emissions for entire version */
  emissions: EmissionFragment[]
}

export function mapVersionToCalculatedVersion(
  version: ProjectVersionFragment,
  energyCalculator: VersionCalculator | null,
): CalculatedVersion | null {
  if (!energyCalculator) {
    return null
  }
  return {
    id: version.id,
    name: version.name,
    energyCalculator,
    totalArea: version.buildings
      .map((building) => building.area * building.multiplier)
      .reduce(reduceToSum, 0),
    buildingIdsToAreas: version.buildings.reduce(
      (records, building) => ({
        ...records,
        [building.id]: building.area,
      }),
      {} as Record<string, number>,
    ),
    emissions: version.buildings.flatMap((building) => building.emissions),
  }
}

export function useCalculator(): UseCalculator {
  const { enqueueError } = useNotifications()
  const { t } = useTranslation()

  return {
    getCalculatedEnergies(
      project: ProjectFragment | null,
      version: ProjectVersionFragment | null,
    ): VersionCalculator | null {
      if (!project || !version) {
        return null
      }
      try {
        return calculateVersionEnergies(project, version)
      } catch (error) {
        if (!(error instanceof CalculatorError)) {
          throw error
        }

        const translation = error.getTranslation()
        const message = t(translation.key, translation.context)

        enqueueError(t('calculator.error.base', { message }))
      }
      return null
    },
  }
}
